import React, { useCallback, useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import QRCode from '../vendor/qrcode';
import QRErrorCorrectLevel from '../vendor/qrcode/QRCode/QRErrorCorrectLevel';
import {
  decideAppCommunityJoinRequest,
  getAppCommunities,
  getAppCommunityDetails,
  sendAppCommunityInvites,
  updateAppCommunity,
} from '../services/api';

const dim = { color:'var(--text-dim)', fontSize:11 };

function GroupQRCode({ value }) {
  const qr = new QRCode(-1, QRErrorCorrectLevel.M);
  qr.addData(String(value));
  qr.make();
  const count = qr.getModuleCount();
  const quietZone = 4;
  return <div style={{ padding:12, background:'#fff', borderRadius:8, width:'fit-content' }}>
    <svg role="img" aria-label={`Group QR code ${value}`} viewBox={`0 0 ${count + quietZone * 2} ${count + quietZone * 2}`} width="220" height="220" style={{ display:'block', imageRendering:'pixelated' }}>
      <rect width="100%" height="100%" fill="#fff" />
      {Array.from({ length:count }, (_, row) => Array.from({ length:count }, (_, column) => qr.isDark(row, column)
        ? <rect key={`${row}-${column}`} x={column + quietZone} y={row + quietZone} width="1" height="1" fill="#071111" /> : null))}
    </svg>
    <div style={{ color:'#071111', fontFamily:'monospace', fontSize:9, textAlign:'center', maxWidth:220, overflowWrap:'anywhere' }}>{value}</div>
  </div>;
}

function Stat({ label, value }) {
  return <div style={{ padding:13, border:'1px solid var(--border)', borderRadius:8 }}>
    <div style={{ fontSize:20, color:'var(--accent)', fontWeight:700 }}>{value || 0}</div>
    <div style={{ marginTop:3, fontSize:10, color:'var(--text-dim)' }}>{label}</div>
  </div>;
}

function GroupDetailsModal({ communityId, onClose, toast, onChanged }) {
  const { auth } = useApp();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decisionLoading, setDecisionLoading] = useState('');
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sendingInvites, setSendingInvites] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAppCommunityDetails(auth?.token, communityId);
      const loaded = response.data || response.community || null;
      setGroup(loaded);
      if (loaded) {
        const settings = loaded.communitySettings || {};
        const membership = settings.membershipSettings || {};
        setEdit({
          name:loaded.name || '', shortDescription:settings.shortDescription || settings.description || '',
          location:loaded.location || '', address:loaded.address || '', city:loaded.city || '',
          visibility:settings.visibility || 'public', isActive:loaded.isActive !== false,
          rules:(settings.communityRules || []).join('\n'), minimumAge:String(membership.minimumAge || 18),
          defaultCapacity:String(membership.defaultCapacity || 16), gender:membership.gender || 'mixed',
          approvalProcess:membership.approvalProcess || 'manual', skillLevels:membership.skillLevels || [],
        });
        setImageFile(null);
        setImagePreview(loaded.logoUrl || loaded.imageUrl || loaded.coverImage || loaded.communitySettings?.logoUrl || loaded.communitySettings?.coverImage || '');
      }
    } catch (error) {
      toast?.(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [auth?.token, communityId, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const escape = event => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', escape);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', escape);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const decide = async (requestId, decision) => {
    setDecisionLoading(requestId);
    try {
      await decideAppCommunityJoinRequest(auth?.token, communityId, requestId, decision);
      toast?.(decision === 'approve' ? 'Join request approved.' : 'Join request rejected.');
      await load();
      onChanged?.();
    } catch (error) {
      toast?.(error.message, 'error');
    } finally {
      setDecisionLoading('');
    }
  };

  const sendInvites = async event => {
    event.preventDefault();
    const emails = inviteEmails.split(/[\n,;]/).map(value => value.trim()).filter(Boolean);
    if (!emails.length) { toast?.('Enter at least one recipient email.', 'error'); return; }
    setSendingInvites(true);
    try {
      const response = await sendAppCommunityInvites(auth?.token, communityId, emails, inviteMessage.trim());
      toast?.(response.message || 'Invitations sent successfully.');
      setInviteEmails('');
      setInviteMessage('');
    } catch (error) {
      toast?.(error.message, 'error');
    } finally {
      setSendingInvites(false);
    }
  };

  const updateEdit = (field, value) => setEdit(previous => ({ ...previous, [field]:value }));
  const toggleSkill = skill => setEdit(previous => ({
    ...previous,
    skillLevels:previous.skillLevels.includes(skill) ? previous.skillLevels.filter(value => value !== skill) : [...previous.skillLevels, skill],
  }));
  const saveChanges = async event => {
    event.preventDefault();
    if (!edit.name.trim() || !edit.location.trim()) { toast?.('Group name and location are required.', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        name:edit.name.trim(), shortDescription:edit.shortDescription.trim(), location:edit.location.trim(),
        address:edit.address.trim(), city:edit.city.trim(), visibility:edit.visibility, isActive:edit.isActive,
        communityRules:edit.rules.split('\n').map(value => value.trim()).filter(Boolean),
        membershipSettings:{
          minimumAge:Number(edit.minimumAge), defaultCapacity:Number(edit.defaultCapacity), gender:edit.gender,
          approvalProcess:edit.approvalProcess, skillLevels:edit.skillLevels,
        },
      };
      if (imageFile) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value)));
        formData.append('logo', imageFile);
        formData.append('coverImage', imageFile);
        await updateAppCommunity(auth?.token, communityId, formData);
      } else {
        await updateAppCommunity(auth?.token, communityId, payload);
      }
      toast?.('Group updated successfully.');
      setEditing(false);
      await load();
      onChanged?.();
    } catch (error) {
      toast?.(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return <div role="dialog" aria-modal="true" aria-label="Group details" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}
    style={{ position:'fixed', inset:0, zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24, background:'rgba(0,0,0,.76)' }}>
    <div className="card" style={{ width:'min(900px,100%)', maxHeight:'88vh', overflowY:'auto', padding:24, boxShadow:'0 24px 70px rgba(0,0,0,.55)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16 }}>
        <div style={{ minWidth:0 }}>
          <div className="card-title">{loading ? 'Loading group details…' : group?.name || 'Group details'}</div>
          {group && <div style={{ ...dim, marginTop:5 }}>{group.organizerName || 'Unassigned'} · {group.location || 'No location'}</div>}
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {group && <button type="button" className="btn btn-primary" onClick={() => setEditing(previous => !previous)}>{editing ? 'Cancel editing' : 'Edit group'}</button>}
          {group && <button type="button" className="btn btn-ghost" style={{ color:'#ff4444', borderColor:'rgba(255,68,68,0.3)' }} onClick={async () => {
            if (!window.confirm('Delete this group? This cannot be undone.')) return;
            try {
              const API = '/api/app';
              const res = await fetch(`${API}/communities/${communityId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${auth?.token}` } });
              if (res.ok) { toast?.('Group deleted'); onChanged?.(); onClose(); }
              else { const d = await res.json(); toast?.(d.message || 'Failed to delete group'); }
            } catch(e) { toast?.('Error deleting group'); }
          }}>Delete group</button>}
          <button type="button" className="btn btn-ghost" onClick={onClose} aria-label="Close group details">Close</button>
        </div>
      </div>

      {loading && <div style={{ ...dim, padding:'35px 0' }}>Loading group details…</div>}
      {!loading && !group && <div style={{ ...dim, padding:'35px 0' }}>Group details could not be loaded.</div>}
      {!loading && group && <>
        {editing && edit && <form onSubmit={saveChanges} style={{ marginTop:20, padding:18, border:'1px solid var(--border)', borderRadius:10, background:'rgba(255,255,255,.015)' }}>
          <div className="card-title" style={{ marginBottom:14 }}>Edit group settings</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,minmax(0,1fr))', gap:12 }}>
            <label style={dim}>Group name<input className="search-input" style={{ width:'100%', marginTop:5 }} value={edit.name} onChange={event => updateEdit('name', event.target.value)} /></label>
            <label style={dim}>Visibility<select className="search-input" style={{ width:'100%', marginTop:5 }} value={edit.visibility} onChange={event => updateEdit('visibility', event.target.value)}><option value="public">Public</option><option value="private">Private</option></select></label>
            <label style={dim}>Location<input className="search-input" style={{ width:'100%', marginTop:5 }} value={edit.location} onChange={event => updateEdit('location', event.target.value)} /></label>
            <label style={dim}>City<input className="search-input" style={{ width:'100%', marginTop:5 }} value={edit.city} onChange={event => updateEdit('city', event.target.value)} /></label>
            <label style={{ ...dim, gridColumn:'1 / -1' }}>Address<input className="search-input" style={{ width:'100%', marginTop:5 }} value={edit.address} onChange={event => updateEdit('address', event.target.value)} /></label>
            <label style={{ ...dim, gridColumn:'1 / -1' }}>Description<textarea className="search-input" style={{ width:'100%', minHeight:70, resize:'vertical', marginTop:5 }} value={edit.shortDescription} onChange={event => updateEdit('shortDescription', event.target.value)} /></label>
            <label style={{ ...dim, gridColumn:'1 / -1' }}>Group image (optional)
              <input type="file" accept="image/*" className="search-input" style={{ width:'100%', marginTop:5 }} onChange={event => {
                const file = event.target.files?.[0] || null;
                setImageFile(file);
                if (file) setImagePreview(URL.createObjectURL(file));
              }} />
              {imagePreview && <img src={imagePreview} alt="Group preview" style={{ display:'block', width:180, height:110, objectFit:'cover', borderRadius:9, border:'1px solid var(--border)', marginTop:10 }} />}
            </label>
            <label style={dim}>Minimum age<input type="number" min="1" max="100" className="search-input" style={{ width:'100%', marginTop:5 }} value={edit.minimumAge} onChange={event => updateEdit('minimumAge', event.target.value)} /></label>
            <label style={dim}>Default capacity<input type="number" min="2" max="1000" className="search-input" style={{ width:'100%', marginTop:5 }} value={edit.defaultCapacity} onChange={event => updateEdit('defaultCapacity', event.target.value)} /></label>
            <label style={dim}>Gender<select className="search-input" style={{ width:'100%', marginTop:5 }} value={edit.gender} onChange={event => updateEdit('gender', event.target.value)}><option value="mixed">Mixed</option><option value="male">Male</option><option value="female">Female</option></select></label>
            <label style={dim}>Join approval<select className="search-input" style={{ width:'100%', marginTop:5 }} value={edit.approvalProcess} onChange={event => updateEdit('approvalProcess', event.target.value)}><option value="manual">Manual approval</option><option value="automatic">Automatic approval</option></select></label>
            <label style={{ ...dim, gridColumn:'1 / -1' }}>Community rules<textarea className="search-input" style={{ width:'100%', minHeight:90, resize:'vertical', marginTop:5 }} value={edit.rules} onChange={event => updateEdit('rules', event.target.value)} /></label>
            <div style={{ gridColumn:'1 / -1' }}><div style={dim}>Skill levels</div><div style={{ display:'flex', flexWrap:'wrap', gap:8, marginTop:7 }}>{[['casual','Casual'],['amateur','Amateur'],['semi_pro','Semi-Pro'],['professional','Professional']].map(([id,label]) => <button key={id} type="button" className={`btn ${edit.skillLevels.includes(id) ? 'btn-primary' : 'btn-ghost'}`} onClick={() => toggleSkill(id)}>{label}</button>)}</div></div>
            <label style={{ ...dim, display:'flex', alignItems:'center', gap:8, gridColumn:'1 / -1' }}><input type="checkbox" checked={edit.isActive} onChange={event => updateEdit('isActive', event.target.checked)} /> Group is active</label>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}><button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save group changes'}</button></div>
        </form>}
        <div style={{ display:'grid', gridTemplateColumns:'auto minmax(0,1fr)', gap:16, margin:'20px 0', alignItems:'start' }}>
          {group.logoUrl && <img src={group.logoUrl} alt="" style={{ width:76, height:76, objectFit:'cover', borderRadius:12, border:'1px solid var(--border)' }} />}
          <div>
            <div style={{ color:'var(--text-primary)', fontSize:12 }}>{group.shortDescription || group.description || 'No description supplied.'}</div>
            <div style={{ ...dim, marginTop:8 }}>Group ID: {group.id}</div>
            {group.address && <div style={{ ...dim, marginTop:4 }}>{group.address}</div>}
          </div>
        </div>

        <div style={{ marginBottom:22 }}>
          <div className="card-title" style={{ marginBottom:10 }}>Group QR code</div>
          <div style={{ display:'flex', gap:24, flexWrap:'wrap' }}>
            <div>
              <div style={{ ...dim, fontSize:10, textTransform:'uppercase', marginBottom:6 }}>Unique QR (this group)</div>
              <GroupQRCode value={group.uniqueQrCodeValue || group.inviteUrl || `https://api.transferexpo.com/invite/${group.id}`} />
              <div style={{ ...dim, marginTop:5, fontSize:11 }}>Opens directly to this community</div>
              {group.uniqueQrCodeValue && (
                <div style={{ marginTop:4, fontSize:9, fontFamily:'monospace', color:'var(--text-dim)', wordBreak:'break-all', maxWidth:220 }}>
                  {group.uniqueQrCodeValue}
                </div>
              )}
            </div>

          </div>
          {group.deepLink && (
            <div style={{ marginTop:10, padding:'8px 12px', background:'var(--bg-input)', borderRadius:6, fontSize:11, fontFamily:'monospace', color:'var(--accent)' }}>
              Deep link: {group.deepLink}
            </div>
          )}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,minmax(0,1fr))', gap:10, marginBottom:24 }}>
          <Stat label="Members" value={group.stats?.members} />
          <Stat label="Matches" value={group.stats?.matches} />
          <Stat label="Active matches" value={group.stats?.activeMatches} />
          <Stat label="Pending requests" value={group.stats?.pendingRequests} />
          <div style={{ padding:13, border:'1px solid var(--border)', borderRadius:8, background: group.groupRating ? 'rgba(167,244,50,0.05)' : 'transparent' }}>
            <div style={{ fontSize:20, color:'var(--accent)', fontWeight:700 }}>
              {group.groupRating ? group.groupRating.toFixed(1) : '—'}
              <span style={{ fontSize:11, color:'var(--text-dim)', fontWeight:400, marginLeft:3 }}>/10</span>
            </div>
            <div style={{ marginTop:3, fontSize:10, color:'var(--text-dim)' }}>Group rating</div>
            {group.groupRatingCount > 0 && <div style={{ fontSize:9, color:'var(--text-dim)', marginTop:2 }}>Based on {group.groupRatingCount} ratings</div>}
          </div>
        </div>

        <div className="card-title" style={{ marginBottom:12 }}>Pending join requests</div>
        <div style={{ display:'grid', gap:9 }}>
          {(group.pendingRequests || []).map(request => <div key={request.id} style={{ display:'grid', gridTemplateColumns:'minmax(0,1fr) auto', alignItems:'center', gap:12, padding:12, border:'1px solid var(--border)', borderRadius:8 }}>
            <div>
              <div style={{ color:'var(--text-primary)', fontSize:12 }}>{request.playerName || request.player?.name || 'Player'}</div>
              <div style={{ ...dim, marginTop:3 }}>{request.player?.email || request.metadata?.preferredPosition || request.playerId}</div>
            </div>
            <div style={{ display:'flex', gap:7 }}>
              <button type="button" className="btn btn-primary" disabled={decisionLoading === request.id} onClick={() => decide(request.id, 'approve')}>Accept</button>
              <button type="button" className="btn btn-ghost" disabled={decisionLoading === request.id} onClick={() => decide(request.id, 'reject')}>Reject</button>
            </div>
          </div>)}
          {!(group.pendingRequests || []).length && <div style={{ ...dim, padding:'10px 0' }}>No pending requests.</div>}
        </div>

        {!!(group.matches || []).length && <div style={{ marginTop:24 }}>
          <div className="card-title" style={{ marginBottom:12 }}>Matches</div>
          <div style={{ display:'grid', gap:8 }}>{group.matches.map(match => <div key={match.id} style={{ padding:11, border:'1px solid var(--border)', borderRadius:8 }}>
            <div style={{ color:'var(--text-primary)', fontSize:12 }}>{match.title || match.name || match.matchType || 'Group match'}</div>
            <div style={{ ...dim, marginTop:3 }}>{match.status || 'Scheduled'}{match.matchDate ? ` · ${new Date(match.matchDate).toLocaleString()}` : ''}</div>
          </div>)}</div>
        </div>}

        <form onSubmit={sendInvites} style={{ marginTop:24, paddingTop:20, borderTop:'1px solid var(--border)' }}>
          <div className="card-title" style={{ marginBottom:12 }}>Send invitations</div>
          <textarea className="search-input" style={{ width:'100%', minHeight:78, resize:'vertical' }} value={inviteEmails} onChange={event => setInviteEmails(event.target.value)} placeholder="Recipient emails, separated by commas or new lines" />
          <textarea className="search-input" style={{ width:'100%', minHeight:68, resize:'vertical', marginTop:10 }} value={inviteMessage} onChange={event => setInviteMessage(event.target.value)} placeholder="Optional invitation message" />
          <div style={{ ...dim, marginTop:7 }}>Up to 50 email addresses.{group.inviteUrl ? ` Invite link: ${group.inviteUrl}` : ''}</div>
          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}><button className="btn btn-primary" type="submit" disabled={sendingInvites}>{sendingInvites ? 'Sending…' : 'Send invitations'}</button></div>
        </form>
      </>}
    </div>
  </div>;
}

export default function CommunityGroups({ toast }) {
  const { auth } = useApp();
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page:1, totalPages:1, total:0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAppCommunities(auth?.token, { page, limit:100, search:search.trim() });
      setCommunities(Array.isArray(response.data) ? response.data : []);
      setPagination(response.pagination || { page, totalPages:1, total:(response.data || []).length });
    } catch (error) {
      toast?.(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [auth?.token, toast, page, search]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);
  const closeModal = useCallback(() => setSelectedId(null), []);

  return <div style={{ maxWidth:1100, margin:'0 auto' }}>
    <div style={{ marginBottom:22 }}>
      <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, color:'var(--text-primary)', margin:0 }}>Groups</h1>
      <p style={{ color:'var(--text-dim)', fontSize:13, margin:'5px 0 0' }}>View group details, process join requests, and invite players.</p>
    </div>
    <section className="card" style={{ padding:24 }}>
      <input type="search" className="search-input" style={{ width:'100%', marginBottom:18 }} value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} placeholder="Search all groups by name, organizer or location" />
      {loading ? <div style={{ ...dim, padding:16 }}>Loading groups…</div> : <div style={{ display:'grid', gap:10 }}>
        {communities.map(group => <div key={group.id} style={{ display:'grid', gridTemplateColumns:'minmax(0,1fr) auto', gap:14, alignItems:'center', padding:'13px 15px', border:'1px solid var(--border)', borderRadius:9 }}>
          <div style={{ minWidth:0 }}>
            <div style={{ color:'var(--text-primary)', fontSize:13, fontWeight:600 }}>{group.name}</div>
            <div style={{ ...dim, marginTop:4 }}>{group.organizerName || 'Unassigned'} · {group.location || 'No location'}</div>
          </div>
          <button type="button" className="btn btn-ghost" onClick={() => setSelectedId(group.id)}>View details</button>
        </div>)}
        {!communities.length && <div style={{ ...dim, padding:16 }}>No groups match your search.</div>}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginTop:8 }}>
          <div style={dim}>{pagination.total || 0} groups · Page {pagination.page || page} of {pagination.totalPages || 1}</div>
          <div style={{ display:'flex', gap:8 }}>
            <button type="button" className="btn btn-ghost" disabled={page <= 1 || loading} onClick={() => setPage(value => Math.max(1, value - 1))}>Previous</button>
            <button type="button" className="btn btn-ghost" disabled={page >= (pagination.totalPages || 1) || loading} onClick={() => setPage(value => value + 1)}>Next</button>
          </div>
        </div>
      </div>}
    </section>
    {selectedId && <GroupDetailsModal communityId={selectedId} onClose={closeModal} toast={toast} onChanged={load} />}
  </div>;
}
